/**
 * Sprint 23.8 — MetricCard (shared scorecard sub-primitive).
 *
 * One metric tile: a label, a value, and an optional delta. Presentational —
 * the caller pre-formats `value` and `delta` (this card never reasons about
 * numbers or windows). Extracted byte-for-byte from the Sprint 23.6 v2
 * AgentScorecard card so the workspace home and the agent profile render an
 * identical tile. The delta colour rule lives with the caller (a negative
 * AI-spend delta is good, not danger) and arrives as `deltaTone`.
 */

import { type CSSProperties } from "react";

export interface MetricCardProps {
  label: string;
  /** Pre-formatted value, e.g. "12" or "$3.40". `…` while loading. */
  value: string;
  /** Pre-formatted delta, e.g. "+3", "=", "—", "-$0.40". Omit for no delta. */
  delta?: string;
  /** Green for a positive/good delta; tertiary for everything else. */
  deltaTone?: "success" | "tertiary";
  /** Tooltip on the delta (e.g. "vs prior 7 days"). */
  deltaTitle?: string;
}

export function MetricCard({ label, value, delta, deltaTone = "tertiary", deltaTitle }: MetricCardProps) {
  const deltaColor =
    deltaTone === "success" ? "var(--color-text-success)" : "var(--color-text-tertiary)";
  return (
    <div style={card}>
      <div style={cardLabel}>{label}</div>
      <div style={valueRow}>
        <div style={cardValue}>{value}</div>
        {delta !== undefined && (
          <div style={{ ...cardDelta, color: deltaColor }} title={deltaTitle}>
            {delta}
          </div>
        )}
      </div>
    </div>
  );
}

const card: CSSProperties = {
  background: "var(--color-background-secondary)",
  borderRadius: "var(--border-radius-md)",
  padding: "8px 10px",
  minWidth: 0,
};

const cardLabel: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-secondary)",
};

const valueRow: CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: 5,
  marginTop: 2,
};

const cardValue: CSSProperties = {
  fontSize: 19,
  fontWeight: 500,
  color: "var(--color-text-primary)",
};

const cardDelta: CSSProperties = {
  fontSize: 10,
};
